// Actualizador de esquema MULTI-BASE (Corredata, Fase 2.3).
//
// Una migración se escribe UNA vez y se aplica a la base de cada cliente desde
// aquí, con registro de quién la tiene y quién no. Nació de un dolor real: el
// 2026-08-03 staging iba 3 migraciones atrás y nadie se enteró hasta que un SQL
// falló en pleno trabajo.
//
// Reglas de seguridad (la parte importante):
//   • Se aplica de a UN cliente y se DETIENE al primer error (no arrastra el
//     fallo a los demás).
//   • El servidor rechaza SQL destructivo (ver functions/api/migrar.js).
//   • El SQL debe ser idempotente (if not exists) para poder reintentar.
//   • Se recomienda ensayar en una instancia de prueba antes que en las reales.
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';
import { Plus, X, CheckCircle, AlertTriangle, RefreshCw, Send } from 'lucide-react';

const VACIA = { nombre: '', descripcion: '', sql: '' };

export default function Migraciones({ actor, onSalir }) {
  const [migraciones, setMigraciones] = useState([]);
  const [clientes, setClientes]       = useState([]);
  const [aplicadas, setAplicadas]     = useState([]);   // filas de cliente_migraciones
  const [loading, setLoading]         = useState(true);
  const [nueva, setNueva]             = useState(null); // null | {nombre, descripcion, sql}
  const [guardando, setGuardando]     = useState(false);
  const [abierta, setAbierta]         = useState(null); // id de la migración expandida
  const [corriendo, setCorriendo]     = useState(null); // id de la migración en curso
  const [progreso, setProgreso]       = useState([]);   // [{cliente, ok, error}]

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: m }, { data: c }, { data: a }] = await Promise.all([
      supabase.from('migraciones').select('*').order('nombre'),
      supabase.from('clientes').select('id, nombre, supabase_ref, estado').order('nombre'),
      supabase.from('cliente_migraciones').select('*'),
    ]);
    setMigraciones(m || []); setClientes(c || []); setAplicadas(a || []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const estadoDe = (clienteId, migracionId) =>
    aplicadas.find(x => x.cliente_id === clienteId && x.migracion_id === migracionId) || null;

  const crear = async () => {
    if (!nueva.nombre.trim() || !nueva.sql.trim()) { alert('Ponle nombre y SQL a la migración.'); return; }
    setGuardando(true);
    const { error } = await supabase.from('migraciones').insert({
      nombre: nueva.nombre.trim(), descripcion: nueva.descripcion.trim() || null,
      sql: nueva.sql, creada_por: actor,
    });
    setGuardando(false);
    if (error) { alert('Error: ' + error.message); return; }
    setNueva(null); load();
  };

  // Llama al servidor para correr el SQL en la base de UN cliente.
  const aplicarEnCliente = async (cli, mig) => {
    const { data: { session } } = await supabase.auth.getSession();
    const r = await fetch('/api/migrar', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
      body: JSON.stringify({ accion: 'aplicar', ref: cli.supabase_ref, sql: mig.sql }),
    });
    const j = await r.json();
    const ok = r.ok && j.ok;
    // Queda registro del intento, haya salido bien o mal.
    await supabase.from('cliente_migraciones').upsert({
      cliente_id: cli.id, migracion_id: mig.id,
      estado: ok ? 'aplicada' : 'error',
      detalle: ok ? null : (j.error || 'Error desconocido'),
      aplicada_por: actor, aplicada_en: new Date().toISOString(),
    }, { onConflict: 'cliente_id,migracion_id' });
    return { ok, error: j.error };
  };

  // Aplica a todos los clientes que NO la tienen. Se detiene al primer error.
  const aplicarPendientes = async (mig) => {
    const pendientes = clientes.filter(c => c.supabase_ref && estadoDe(c.id, mig.id)?.estado !== 'aplicada');
    if (!pendientes.length) { alert('Todos los clientes ya tienen esta migración.'); return; }
    const sinRef = clientes.filter(c => !c.supabase_ref).length;
    if (!window.confirm(
      `Se va a correr "${mig.nombre}" en ${pendientes.length} cliente(s).\n\n` +
      `El SQL debe ser idempotente. Si un cliente falla, el proceso se detiene ahí.\n` +
      (sinRef ? `\n(${sinRef} cliente(s) no tienen ref de Supabase y se omiten.)\n` : '') +
      `\n¿Continuar?`)) return;

    setCorriendo(mig.id); setProgreso([]);
    for (const cli of pendientes) {
      setProgreso(p => [...p, { cliente: cli.nombre, estado: 'corriendo' }]);
      let res;
      try { res = await aplicarEnCliente(cli, mig); }
      catch (e) { res = { ok: false, error: e.message || 'Error de red' }; }
      setProgreso(p => p.map(x => x.cliente === cli.nombre ? { ...x, estado: res.ok ? 'ok' : 'error', error: res.error } : x));
      if (!res.ok) {
        setProgreso(p => [...p, { cliente: '—', estado: 'detenido', error: 'Proceso detenido por el error anterior. Arregla la causa y vuelve a intentar.' }]);
        break;
      }
    }
    setCorriendo(null);
    await load();
  };

  // Para migraciones que ya se corrieron a mano (ej. las de RU antes de esta pantalla).
  const marcarAplicada = async (cli, mig) => {
    if (!window.confirm(`Marcar "${mig.nombre}" como YA aplicada en ${cli.nombre}?\n\nUsa esto solo si la corriste a mano; no ejecuta nada.`)) return;
    await supabase.from('cliente_migraciones').upsert({
      cliente_id: cli.id, migracion_id: mig.id, estado: 'aplicada',
      detalle: 'Marcada a mano (se corrió fuera del portal)',
      aplicada_por: actor, aplicada_en: new Date().toISOString(),
    }, { onConflict: 'cliente_id,migracion_id' });
    load();
  };

  const conRef = clientes.filter(c => c.supabase_ref).length;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '22px 20px 60px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>Migraciones de esquema</div>
          <div style={{ fontSize: 12, color: 'var(--faint)' }}>
            Aplica un cambio de base a todos los clientes sin que ninguno se quede atrás · {clientes.length} cliente(s), {conRef} con proyecto enlazado
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-sm" onClick={load}><RefreshCw size={14} /> Actualizar</button>
          <button className="btn btn-primary" onClick={() => setNueva({ ...VACIA })}><Plus size={16} /> Nueva migración</button>
          {onSalir && <button className="btn btn-sm" onClick={onSalir}>Volver</button>}
        </div>
      </div>

      <div className="card" style={{ padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--muted)', lineHeight: 1.6 }}>
        <AlertTriangle size={13} style={{ verticalAlign: 'middle', marginRight: 6, color: 'var(--warning, #f59e0b)' }} />
        El SQL debe ser <b>idempotente</b> (<code>if not exists</code>) para poder reintentarlo sin daño. El servidor rechaza
        DROP / TRUNCATE / DELETE sin WHERE. Se aplica de a un cliente y <b>se detiene al primer error</b>.
        Ensaya siempre en una instancia de prueba antes que en las reales.
      </div>

      {loading ? (
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--faint)' }}>Cargando…</div>
      ) : !migraciones.length ? (
        <div className="card" style={{ padding: 48, textAlign: 'center', color: 'var(--faint)' }}>
          Aún no hay migraciones registradas.
        </div>
      ) : migraciones.map(mig => {
        const aplicadasN = clientes.filter(c => estadoDe(c.id, mig.id)?.estado === 'aplicada').length;
        const conError   = clientes.filter(c => estadoDe(c.id, mig.id)?.estado === 'error').length;
        const completa   = aplicadasN === conRef && conRef > 0;
        const expandida  = abierta === mig.id;
        return (
          <div key={mig.id} className="card" style={{ marginBottom: 10, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
              onClick={() => setAbierta(expandida ? null : mig.id)}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{mig.nombre}</div>
                {mig.descripcion && <div style={{ fontSize: 12, color: 'var(--muted)' }}>{mig.descripcion}</div>}
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                color: completa ? 'var(--success, #22c55e)' : conError ? 'var(--danger, #ef4444)' : 'var(--muted)',
                background: completa ? 'rgba(34,197,94,.12)' : conError ? 'rgba(239,68,68,.12)' : 'rgba(148,163,184,.12)' }}>
                {aplicadasN} de {conRef} aplicada(s){conError ? ` · ${conError} con error` : ''}
              </span>
              {!completa && (
                <button className="btn btn-sm btn-primary" disabled={corriendo === mig.id}
                  onClick={(e) => { e.stopPropagation(); aplicarPendientes(mig); }}>
                  <Send size={13} /> {corriendo === mig.id ? 'Aplicando…' : 'Aplicar a los que faltan'}
                </button>
              )}
            </div>

            {expandida && (
              <div style={{ borderTop: '1px solid rgba(148,163,184,.15)', padding: '12px 16px' }}>
                <pre style={{ margin: '0 0 12px', padding: 10, background: 'rgba(148,163,184,.08)', borderRadius: 8,
                  fontSize: 11, whiteSpace: 'pre-wrap', color: 'var(--muted)' }}>{mig.sql}</pre>
                <table>
                  <thead><tr><th>Cliente</th><th>Estado</th><th>Detalle</th><th></th></tr></thead>
                  <tbody>
                    {clientes.map(c => {
                      const e = estadoDe(c.id, mig.id);
                      return (
                        <tr key={c.id}>
                          <td style={{ fontWeight: 600 }}>{c.nombre}{!c.supabase_ref && <span style={{ color: 'var(--faint)', fontWeight: 400 }}> · sin ref</span>}</td>
                          <td>
                            {e?.estado === 'aplicada'
                              ? <span style={{ color: 'var(--success, #22c55e)', fontSize: 12, fontWeight: 700 }}>✓ aplicada</span>
                              : e?.estado === 'error'
                                ? <span style={{ color: 'var(--danger, #ef4444)', fontSize: 12, fontWeight: 700 }}>✗ error</span>
                                : <span style={{ color: 'var(--faint)', fontSize: 12 }}>pendiente</span>}
                          </td>
                          <td style={{ fontSize: 11, color: 'var(--muted)' }}>{e?.detalle || (e?.aplicada_en ? new Date(e.aplicada_en).toLocaleString() : '—')}</td>
                          <td style={{ textAlign: 'right' }}>
                            {e?.estado !== 'aplicada' && (
                              <button className="btn btn-sm" onClick={() => marcarAplicada(c, mig)}
                                title="Solo marca; no ejecuta nada. Para migraciones ya corridas a mano.">
                                <CheckCircle size={12} /> Ya la tiene
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {corriendo === mig.id && progreso.length > 0 && (
              <div style={{ borderTop: '1px solid rgba(148,163,184,.15)', padding: '10px 16px', fontSize: 12 }}>
                {progreso.map((p, i) => (
                  <div key={i} style={{ color: p.estado === 'ok' ? 'var(--success, #22c55e)' : p.estado === 'error' || p.estado === 'detenido' ? 'var(--danger, #ef4444)' : 'var(--muted)' }}>
                    {p.estado === 'ok' ? '✓' : p.estado === 'error' ? '✗' : p.estado === 'detenido' ? '⛔' : '…'} {p.cliente} {p.error ? `— ${p.error}` : ''}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Nueva migración */}
      {nueva && (
        <div className="modal-overlay" onClick={() => !guardando && setNueva(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div className="card" style={{ width: 'min(680px, 92vw)', padding: 20 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ fontSize: 16, fontWeight: 800 }}>Nueva migración</div>
              <button className="btn btn-sm" onClick={() => setNueva(null)}><X size={14} /></button>
            </div>
            <label style={{ fontSize: 12, color: 'var(--muted)' }}>Nombre (único)</label>
            <input className="input" style={{ width: '100%', marginBottom: 10 }} placeholder="2026-08-10-descripcion-corta"
              value={nueva.nombre} onChange={e => setNueva({ ...nueva, nombre: e.target.value })} />
            <label style={{ fontSize: 12, color: 'var(--muted)' }}>Descripción</label>
            <input className="input" style={{ width: '100%', marginBottom: 10 }} placeholder="qué agrega y para qué"
              value={nueva.descripcion} onChange={e => setNueva({ ...nueva, descripcion: e.target.value })} />
            <label style={{ fontSize: 12, color: 'var(--muted)' }}>SQL (idempotente)</label>
            <textarea className="input" rows={8} style={{ width: '100%', fontFamily: 'monospace', fontSize: 12 }}
              placeholder={'alter table X add column if not exists Y tipo;'}
              value={nueva.sql} onChange={e => setNueva({ ...nueva, sql: e.target.value })} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
              <button className="btn btn-sm" onClick={() => setNueva(null)} disabled={guardando}>Cancelar</button>
              <button className="btn btn-primary" onClick={crear} disabled={guardando}>{guardando ? 'Guardando…' : 'Registrar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
